import { CONFERENCE_JOINED, CONFERENCE_LEFT, DATA_CHANNEL_OPENED, P2P_STATUS_CHANGED, } from "../base/conference/actionTypes";
import { getCurrentConference } from "../base/conference/functions";
import { openDialog } from "../base/dialog/actions";
import { PARTICIPANT_JOINED, PARTICIPANT_LEFT, PARTICIPANT_UPDATED } from "../base/participants/actionTypes";
import { getRemoteParticipants } from "../base/participants/functions";
import MiddlewareRegistry from "../base/redux/MiddlewareRegistry";
import { setVolume } from "../filmstrip/actions.web";
import { showSuccessNotification, showWarningNotification } from "../notifications/actions";
import { NOTIFICATION_TIMEOUT_TYPE } from "../notifications/constants";
import { SET_SPOKEN_LANGUAGE } from "./actionTypes";
import SpokenLanguageSelectorDialog from "./components/web/SpokenLanguageSelectorDialog";
import { DUCKING_VOLUME, NORMAL_VOLUME } from "./constants";
import { getSpokenLanguage } from "./functions";
import logger from "./logger";
// Import reducer to ensure it gets registered
import "./reducer";
/**
 * The display name prefix for translator participants.
 * Translator agents join with names like 'translator-en', 'translator-hi'.
 */
const TRANSLATOR_DISPLAY_NAME_PREFIX = "translator-";
/**
 * Tracks whether the user has explicitly selected a language via the UI.
 * When false, all translator audio is excluded (no translation by default).
 * Set to true when SET_SPOKEN_LANGUAGE is dispatched.
 */
let _hasExplicitSelection = false;
/**
 * Middleware that manages audio subscriptions for live translation.
 *
 * Uses the Receiver Audio Subscriptions API (conference.setAudioSubscriptionMode)
 * to selectively control which translator audio each client receives from JVB.
 *
 * Strategy: Use "Exclude" mode to exclude all translator participants by default.
 * When the user explicitly selects a language, exclude all translators except
 * the one matching the selected language.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register((store) => (next) => (action) => {
    // Let the action update Redux state first so getSpokenLanguage,
    // getRemoteParticipants, and getCurrentConference return current values.
    const result = next(action);
    switch (action.type) {
        case CONFERENCE_JOINED:
            // Apply audio subscription based on explicit selection state.
            // May silently fail if bridge channel isn't open yet.
            logger.info("Conference joined, applying initial audio subscription");
            _updateAudioSubscription(store);
            _updateParticipantVolumes(store);
            // Broadcast spoken language as a participant property so translator
            // bots can identify which participants speak which language and
            // exclude same-language speakers from audio capture (self-echo prevention).
            _broadcastSpokenLanguage(store);
            // If no language has been selected (e.g., pre-join screen was skipped),
            // show a mandatory language selection dialog.
            _promptLanguageSelectionIfNeeded(store);
            break;
        case CONFERENCE_LEFT:
            // Reset selection state so the next conference starts fresh
            // (no stale subscription from a prior session).
            logger.info("Conference left, resetting selection state and volumes");
            _resetParticipantVolumes(store);
            _hasExplicitSelection = false;
            break;
        case DATA_CHANNEL_OPENED:
            // Bridge channel just opened. Force-resend audio subscription
            // in case the CONFERENCE_JOINED call was silently dropped
            // (RTC.sendReceiverAudioSubscriptionMessage drops messages
            // when the channel is not yet open, unlike video constraints
            // which are cached and sent on channel open).
            logger.info("Data channel opened, force-resending audio subscription");
            _forceUpdateAudioSubscription(store);
            break;
        case P2P_STATUS_CHANGED:
            // When P2P session ends (p2p becomes false), we return to JVB mode.
            // The bridge channel subscription state may have been lost during P2P.
            // Force-resend audio subscription to ensure JVB applies our filters.
            if (!action.p2p) {
                logger.info("P2P ended, forcing audio subscription update for JVB");
                _forceUpdateAudioSubscription(store);
            }
            else {
                logger.info("P2P started — audio subscription has no effect in P2P mode");
            }
            break;
        case SET_SPOKEN_LANGUAGE:
            logger.info(`Spoken language set to "${action.language}", hasExplicitSelection=true`);
            _hasExplicitSelection = true;
            _updateAudioSubscription(store);
            _updateParticipantVolumes(store);
            _broadcastSpokenLanguage(store);
            _notifyLanguageChange(store, action.language);
            break;
        case PARTICIPANT_JOINED:
            // If a translator agent joined, update exclusion list
            if (_isTranslatorName(action.participant?.name)) {
                logger.info(`Translator joined: name="${action.participant?.name}", id=${action.participant?.id}`);
                _updateAudioSubscription(store);
                _notifyTranslatorArrival(store, action.participant?.name);
            }
            // Duck new other-language participants (applies to all, not just translators)
            _updateParticipantVolumes(store);
            break;
        case PARTICIPANT_UPDATED: {
            // Re-evaluate audio subscription and ducking when remote participants change.
            // Skip local participant updates (name typing per keystroke is irrelevant).
            const updatedParticipant = action.participant;
            if (!updatedParticipant?.local) {
                if (updatedParticipant?.name !== undefined) {
                    logger.debug(`Participant updated: id=${updatedParticipant.id}, name="${updatedParticipant.name}"`);
                    _updateAudioSubscription(store);
                }
                // When a participant changes their spoken language,
                // re-evaluate ducking volumes for all participants.
                if (updatedParticipant?.spokenLanguage !== undefined) {
                    logger.info(`Participant spokenLanguage changed: id=${updatedParticipant.id}, lang="${updatedParticipant.spokenLanguage}"`);
                    _updateParticipantVolumes(store);
                }
            }
            break;
        }
        case PARTICIPANT_LEFT:
            // Cannot check name (PARTICIPANT_LEFT only carries id).
            // Always re-evaluate; ReceiveAudioController deduplicates
            // identical messages so this is cheap when nothing changes.
            logger.info(`Participant left: id=${action.participant?.id}`);
            _updateAudioSubscription(store);
            break;
    }
    return result;
});
/**
 * Checks whether a display name belongs to a translator participant.
 *
 * @param {string | undefined} name - The display name.
 * @returns {boolean}
 */
function _isTranslatorName(name) {
    return Boolean(name) && name.toLowerCase().startsWith(TRANSLATOR_DISPLAY_NAME_PREFIX);
}
/**
 * Returns all remote participants that are translator agents.
 *
 * @param {IStore} store - The redux store.
 * @returns {IParticipant[]}
 */
function _getTranslatorParticipants(store) {
    const state = store.getState();
    const remoteParticipants = getRemoteParticipants(state);
    const translators = [];
    for (const [, participant] of remoteParticipants) {
        if (_isTranslatorName(participant.name)) {
            translators.push(participant);
        }
    }
    return translators;
}
/**
 * Broadcasts the user's spoken language as a participant property via XMPP presence.
 * Translator bots listen for this property to know which participants speak which
 * language, enabling them to exclude same-language speakers from audio capture
 * and prevent self-echo.
 *
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function _broadcastSpokenLanguage(store) {
    if (!_hasExplicitSelection) {
        return;
    }
    const state = store.getState();
    const conference = getCurrentConference(state);
    if (!conference) {
        return;
    }
    const spokenLanguage = getSpokenLanguage(state);
    if (!spokenLanguage) {
        return;
    }
    if (typeof conference.setLocalParticipantProperty === "function") {
        logger.info(`Broadcasting spokenLanguage="${spokenLanguage}" as participant property`);
        conference.setLocalParticipantProperty("spokenLanguage", spokenLanguage);
    }
}
/**
 * Forces an audio subscription update, bypassing ReceiveAudioController's
 * deduplication logic.
 *
 * When CONFERENCE_JOINED fires before the bridge data channel is open,
 * ReceiveAudioController updates its internal state but the message is
 * silently dropped by RTC (channel not open). A subsequent call with
 * identical params is then deduped. This function resets the controller
 * to "All" first, which clears the internal state, then calls
 * _updateAudioSubscription to send the correct subscription.
 *
 * Both calls are synchronous, so there is no window where incorrect
 * audio is received.
 *
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function _forceUpdateAudioSubscription(store) {
    const state = store.getState();
    const conference = getCurrentConference(state);
    if (!conference) {
        return;
    }
    if (typeof conference.setAudioSubscriptionMode !== "function") {
        logger.info("setAudioSubscriptionMode not available on conference object");
        return;
    }
    // Reset ReceiveAudioController's internal state to bypass deduplication.
    logger.info("Force-resetting to All before reapplying subscription (dedup bypass)");
    conference.setAudioSubscriptionMode({ mode: "All" });
    // Now send the actual subscription (no longer deduped since state was reset).
    _updateAudioSubscription(store);
}
/**
 * Computes and sends the correct audio subscription to JVB.
 *
 * - No translators present: resets to "All" mode (default).
 * - No explicit selection: excludes ALL translator IDs (no translation audio).
 * - Explicit selection: excludes all translator IDs except the one matching
 *   the user's selected language.
 *
 * The ReceiveAudioController in lib-jitsi-meet deduplicates identical messages
 * (same mode + same list), so calling this function redundantly is safe.
 *
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function _updateAudioSubscription(store) {
    const state = store.getState();
    const conference = getCurrentConference(state);
    if (!conference) {
        return;
    }
    // Guard: setAudioSubscriptionMode may not exist on older JVB/lib-jitsi-meet
    if (typeof conference.setAudioSubscriptionMode !== "function") {
        logger.info("setAudioSubscriptionMode not available, skipping");
        return;
    }
    const translators = _getTranslatorParticipants(store);
    if (translators.length === 0) {
        // No translators in the room — reset to default so we don't
        // leave a stale exclusion list from a previous state.
        logger.info("No translators present, setting mode=All");
        conference.setAudioSubscriptionMode({ mode: "All" });
        return;
    }
    // If the user hasn't explicitly selected a language, exclude ALL translators.
    if (!_hasExplicitSelection) {
        const excludeSources = translators.map((t) => `${t.id}-a0`);
        logger.info(`No explicit selection, excluding ${translators.length} translator(s):` +
            ` [${translators.map((t) => `${t.id}(${t.name})`).join(", ")}]` +
            ` sourceNames=[${excludeSources.join(", ")}]`);
        conference.setAudioSubscriptionMode({
            mode: "Exclude",
            list: excludeSources,
        });
        return;
    }
    // User has explicitly selected a language — let through the matching translator.
    const spokenLanguage = getSpokenLanguage(state);
    if (!spokenLanguage) {
        // Defensive: _hasExplicitSelection is true but no language in state.
        // Treat as "no selection" — exclude all translators.
        const excludeSources = translators.map((t) => `${t.id}-a0`);
        logger.info(`Explicit selection but no language in state, excluding all translators`);
        conference.setAudioSubscriptionMode({
            mode: "Exclude",
            list: excludeSources,
        });
        return;
    }
    const selectedName = `${TRANSLATOR_DISPLAY_NAME_PREFIX}${spokenLanguage}`.toLowerCase();
    // Exclude every translator EXCEPT the one that matches the user's language.
    // Use audio source names ({endpointId}-a0) — JVB matches against AudioSourceDesc.sourceName.
    const excludeSources = translators.filter((t) => t.name?.toLowerCase() !== selectedName).map((t) => `${t.id}-a0`);
    if (excludeSources.length === 0) {
        // The only translator(s) present match the selected language.
        // No need to exclude anything.
        logger.info(`All translators match selected language "${spokenLanguage}", setting mode=All`);
        conference.setAudioSubscriptionMode({ mode: "All" });
    }
    else {
        logger.info(`Excluding ${excludeSources.length} translator(s), keeping "${selectedName}",` +
            ` mode=Exclude, sourceNames=[${excludeSources.join(", ")}]`);
        conference.setAudioSubscriptionMode({
            mode: "Exclude",
            list: excludeSources,
        });
    }
}
/**
 * Updates the volume of all remote participants based on their spoken language
 * relative to the user's selected language.
 *
 * When translation is active, other-language speakers are ducked to DUCKING_VOLUME
 * (15%) so the TTS translation dominates. Same-language speakers and participants
 * with unknown language remain at full volume.
 *
 * Uses the existing filmstrip setVolume action which sets audio.volume on the
 * HTMLAudioElement via the AudioTrack component.
 *
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function _updateParticipantVolumes(store) {
    const state = store.getState();
    const conference = getCurrentConference(state);
    if (!conference) {
        return;
    }
    const spokenLanguage = getSpokenLanguage(state);
    const remoteParticipants = getRemoteParticipants(state);
    const { dispatch } = store;
    for (const [id, participant] of remoteParticipants) {
        // Skip translator participants — their volume is always normal.
        if (_isTranslatorName(participant.name)) {
            continue;
        }
        if (!_hasExplicitSelection || !spokenLanguage) {
            // No language selected: everyone at normal volume.
            dispatch(setVolume(id, NORMAL_VOLUME));
            continue;
        }
        const participantLang = participant.spokenLanguage;
        if (participantLang && participantLang !== spokenLanguage) {
            // Different language: duck their audio so TTS translation dominates.
            logger.debug(`Ducking participant ${id} (lang="${participantLang}") to ${DUCKING_VOLUME}`);
            dispatch(setVolume(id, DUCKING_VOLUME));
        }
        else {
            // Same language or unknown: normal volume.
            dispatch(setVolume(id, NORMAL_VOLUME));
        }
    }
}
/**
 * Resets all remote participant volumes to normal.
 * Called when leaving a conference to clean up ducking state.
 *
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function _resetParticipantVolumes(store) {
    const state = store.getState();
    const remoteParticipants = getRemoteParticipants(state);
    const { dispatch } = store;
    for (const [id, participant] of remoteParticipants) {
        if (_isTranslatorName(participant.name)) {
            continue;
        }
        dispatch(setVolume(id, NORMAL_VOLUME));
    }
}
/**
 * Shows the language selection dialog if the user hasn't selected a spoken
 * language yet. This handles the case where the pre-join screen is skipped
 * or language selection was not completed before joining.
 *
 * The dialog is non-dismissable until a language is selected.
 *
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function _promptLanguageSelectionIfNeeded(store) {
    const state = store.getState();
    const spokenLanguage = getSpokenLanguage(state);
    if (!spokenLanguage) {
        logger.info("No spoken language selected, showing mandatory language selection dialog");
        store.dispatch(openDialog("SpokenLanguageSelectorDialog", SpokenLanguageSelectorDialog));
    }
}
/**
 * Shows a notification after the user changes their spoken language.
 *
 * @param {IStore} store - The redux store.
 * @param {SpokenLanguage} language - The newly selected language.
 * @returns {void}
 */
function _notifyLanguageChange(store, language) {
    const { dispatch } = store;
    const translators = _getTranslatorParticipants(store);
    const targetName = `${TRANSLATOR_DISPLAY_NAME_PREFIX}${language}`.toLowerCase();
    const found = translators.some((t) => t.name?.toLowerCase() === targetName);
    if (found) {
        dispatch(showSuccessNotification({
            titleKey: "translation.subscriptionSuccess",
            descriptionKey: "translation.subscriptionSuccessDescription",
            descriptionArguments: { language: language.toUpperCase() },
        }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
    }
    else {
        dispatch(showWarningNotification({
            titleKey: "translation.translatorNotFound",
            descriptionKey: "translation.translatorNotFoundDescription",
            descriptionArguments: { language: language.toUpperCase() },
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
    }
}
/**
 * Shows a notification when a translator agent joins the conference,
 * but only if the user has explicitly selected a language and the
 * arriving translator matches that selection.
 *
 * @param {IStore} store - The redux store.
 * @param {string | undefined} translatorName - The translator's display name.
 * @returns {void}
 */
function _notifyTranslatorArrival(store, translatorName) {
    if (!translatorName || !_hasExplicitSelection) {
        return;
    }
    const state = store.getState();
    const spokenLanguage = getSpokenLanguage(state);
    if (!spokenLanguage) {
        return;
    }
    const expectedName = `${TRANSLATOR_DISPLAY_NAME_PREFIX}${spokenLanguage}`.toLowerCase();
    if (translatorName.toLowerCase() === expectedName) {
        store.dispatch(showSuccessNotification({
            titleKey: "translation.subscriptionSuccess",
            descriptionKey: "translation.subscriptionSuccessDescription",
            descriptionArguments: { language: spokenLanguage.toUpperCase() },
        }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
    }
}
